import { AnimatePresence, motion } from 'framer-motion'

import { useEffect, useRef, useState } from 'react'

import { Wifi, WifiOff } from 'lucide-react'

import { useConnectionStatus } from './NetworkStatus'

const OfflineModal = () => {
  const isOnline = useConnectionStatus()
  const [visible, setVisible] = useState(false)
  const [message, setMessage] = useState('Connection lost')
  const [icon, setIcon] = useState(<WifiOff className='h-12 w-12 animate-[pulse_2s_infinite] text-red-600' />)
  const [progress, setProgress] = useState(0)

  const prevOnlineRef = useRef(isOnline)
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null)
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const prevOnline = prevOnlineRef.current

    if (!isOnline && prevOnline) {
      // went offline
      setMessage('Connection lost')
      setIcon(<WifiOff className='h-12 w-12 animate-[pulse_2s_infinite] text-red-600' />)
      setVisible(true)
      setProgress(0)
    } else if (isOnline && !prevOnline) {
      // came back online
      setMessage('Connection restored')
      setIcon(<Wifi className='h-12 w-12 animate-[pulse_2s_infinite] text-green-600' />)
      setVisible(true)
      setProgress(0)

      const duration = 3000 // 3 seconds
      const interval = 50
      const increment = (interval / duration) * 100

      if (progressTimerRef.current) clearInterval(progressTimerRef.current)
      progressTimerRef.current = setInterval(() => {
        setProgress((prev) => {
          const next = prev + increment
          if (next >= 100) {
            clearInterval(progressTimerRef.current!)
            return 100
          }
          return next
        })
      }, interval)

      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
      hideTimerRef.current = setTimeout(() => setVisible(false), 3000)
    }

    prevOnlineRef.current = isOnline

    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
      if (progressTimerRef.current) clearInterval(progressTimerRef.current)
    }
  }, [isOnline])

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key='offline-modal'
          className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className='flex w-full max-w-sm flex-col items-center gap-4 rounded-2xl bg-white p-8 text-center shadow-2xl dark:bg-gray-900'
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {icon}
            <h2 className={`text-lg font-bold ${isOnline ? 'text-green-700' : 'text-red-700'}`}>{message}</h2>
            {!isOnline && (
              <p className='text-sm text-gray-600'>
                The app is offline. Some features may not work until your connection is restored.
              </p>
            )}
            {isOnline && (
              <div className='mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700'>
                <div className='h-2 rounded-full bg-green-500 transition-all' style={{ width: `${progress}%` }} />
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default OfflineModal
